import { defineSemanticTokens } from '@chakra-ui/react'

/**
 * Semantic color tokens — all 96 tokens from the HiveWatch design token map.
 * Source: hivewatch-design-tokens 26-04-16.xlsx → "Color Tokens" sheet, column D
 * Hex values resolved from hivewatch-colors.csv
 *
 * Tokens marked "TBD" in the spreadsheet are omitted pending designer confirmation:
 *   status.guard-badge, status.sop-operator, status.sop-guard, brand.overwatch
 */
export const semanticColors = defineSemanticTokens.colors({
  // ─── Surface / Background ────────────────────────────────────────────────────
  bg: {
    app: { value: '{colors.darkBlue.900}' },
    sidebar: { value: '{colors.darkBlue.500}' },
    'sidebar-hover': { value: '{colors.darkBlue.600}' },
    'sidebar-active': { value: 'transparent' },
    card: { value: '{colors.darkBlue.800}' },
    'card-elevated': { value: '{colors.darkBlue.500}' },
    input: { value: '{colors.duskBlue.800}' },
    'table-row': { value: '{colors.darkBlue.900}' },
    'table-row-alt': { value: '{colors.darkBlue.800}' },
    'table-row-hover': { value: '{colors.darkBlue.700}' },
    // Same surface as hover by design (Dark.tokens.json) — the brand/primary
    // left stripe carries the selection signal.
    'table-row-selected': { value: '{colors.darkBlue.700}' },
    scrim: { value: 'rgba(0, 10, 15, 0.6)' },
    overlay: { value: 'rgba(0, 0, 0, 0.6)' },
    // Popover surfaces (Calendar, floating panels) — Dark.tokens.json bg/popover.
    popover: { value: '{colors.duskBlue.700}' },
    tooltip: { value: '{colors.duskBlue.500}' },
    // Filled background of the SELECTED day in a Calendar. Dawn-blue by design,
    // NOT signal yellow (interactive/primary is reserved for CTAs).
    'calendar-selected': { value: '{colors.dawnBlue.300}' },
    topbar: { value: '{colors.darkBlue.700}' },
    chip: { value: '{colors.signalYellow.400}' },
    'chip-secondary': { value: '{colors.ghostProtocol.950}' },
    'chip-in-field': { value: '{colors.darkBlue.50}' },
    'alert-info': { value: '{colors.duskBlue.900}' },
    'alert-success': { value: '{colors.green.900}' },
    'alert-warning': { value: '{colors.orange.900}' },
    'alert-danger': { value: '{colors.red.900}' },
    // Organization-wide policy surface: the Critical incident sound card and
    // the note beneath the operator's audio controls (ENG-35923). A deep,
    // desaturated green, quieter than alert-success. Value is from the
    // ENG-35923 prototype and is not in the Design Library yet; reconcile
    // when designed.
    'org-policy': { value: '#0a1c14' },
    'segmented-track': { value: '{colors.darkBlue.700}' },
    'segment-active': { value: '{colors.darkBlue.300}' },
    'segment-hover': { value: '{colors.darkBlue.500}' },
    'toggle-track': { value: '{colors.ghostProtocol.700}' },
    'sidebar-account': { value: '{colors.darkBlue.400}' },
    'user-badge': { value: '{colors.dawnBlue.300}' },
    'tab-active': { value: '{colors.darkBlue.400}' },
    callout: { value: '{colors.duskBlue.700}' },
    disabled: { value: '{colors.darkBlue.800}' },
    'filters-btn': { value: '{colors.darkBlue.800}' },
    label: { value: '{colors.overwatch.900}' },
    'label-archived': { value: '{colors.overwatch.950}' },
    'label-more': { value: '{colors.overwatch.900}' },
    'label-tooltip': { value: '{colors.duskBlue.500}' },
    'action-bar': { value: '{colors.duskBlue.500}' },
    'action-bar-hover': { value: '{colors.duskBlue.400}' },
    'action-bar-active': { value: '{colors.duskBlue.700}' },
    'checkbox-on': { value: '{colors.duskBlue.700}' },
    'badge-button': { value: '{colors.darkBlue.50}' },
    'badge-nav': { value: '{colors.signalYellow.400}' },
    'badge-incident': { value: '{colors.red.500}' },
    'notification-dot': { value: '{colors.signalYellow.400}' },
    // Loading placeholder fill (e.g. TopBar count skeleton). Chakra v1's
    // default Skeleton end color (gray.600) so the pulse reads softly.
    skeleton: { value: '#4A5568' },
  },

  // ─── Text / Typography ────────────────────────────────────────────────────────
  text: {
    primary: { value: '{colors.darkBlue.50}' },
    // Over the media overlay scrim (bg.overlay), which is dark in both
    // schemes. Not in the Figma export yet — carried from the legacy v1
    // theme; reconcile when designed. Dark value equals text.primary.
    'on-overlay': { value: '{colors.darkBlue.50}' },
    'on-overlay-muted': { value: 'rgba(230, 234, 237, 0.4)' },
    secondary: { value: '{colors.darkBlue.300}' },
    tertiary: { value: '{colors.ghostProtocol.500}' },
    'input-value': { value: '{colors.darkBlue.200}' },
    'input-disabled': { value: '{colors.darkBlue.400}' },
    disabled: { value: '{colors.ghostProtocol.600}' },
    'on-accent': { value: '{colors.darkBlue.900}' },
    'on-danger': { value: '{colors.darkBlue.50}' },
    link: { value: '{colors.dawnBlue.300}' },
    // Legacy brand blue for incident-reference links (fixed in both modes).
    'incident-link': { value: '{colors.legacy.brandBlue}' },
    'badge-count': { value: '{colors.darkBlue.50}' },
    'badge-button': { value: '{colors.darkBlue.900}' },
    'badge-nav': { value: '{colors.darkBlue.900}' },
    'badge-incident': { value: '{colors.darkBlue.50}' },
    // Day-number text on a selected calendar day; dark navy on the light-blue
    // bg/calendar-selected fill for legible contrast in both modes.
    'calendar-selected': { value: '{colors.darkBlue.900}' },
    'chip-label': { value: '{colors.darkBlue.900}' },
    'chip-in-field': { value: '{colors.darkBlue.800}' },
    'chip-secondary': { value: '{colors.ghostProtocol.400}' },
    'alert-info': { value: '{colors.ghostProtocol.300}' },
    'alert-info-bold': { value: '{colors.darkBlue.50}' },
    'alert-success': { value: '{colors.green.200}' },
    'alert-success-bold': { value: '{colors.green.100}' },
    'alert-danger': { value: '{colors.red.200}' },
    'alert-danger-bold': { value: '{colors.red.300}' },
    'nav-default': { value: '{colors.darkBlue.300}' },
    'nav-active': { value: '{colors.signalYellow.400}' },
    'page-title': { value: '{colors.darkBlue.50}' },
    'count-badge': { value: '{colors.darkBlue.50}' },
    label: { value: '{colors.overwatch.400}' },
    'label-archived': { value: '{colors.overwatch.700}' },
    'label-tooltip': { value: '{colors.darkBlue.50}' },
    'search-placeholder': { value: '{colors.duskBlue.300}' },
    'action-bar-label': { value: '{colors.darkBlue.50}' },
    'action-bar-action': { value: '{colors.darkBlue.50}' },
  },

  // ─── Interactive / Accent ─────────────────────────────────────────────────────
  interactive: {
    primary: { value: '{colors.signalYellow.400}' },
    'primary-hover': { value: '{colors.signalYellow.300}' },
    'primary-active': { value: '{colors.signalYellow.500}' },
    'primary-disabled-bg': { value: '{colors.signalYellow.900}' },
    'primary-disabled-text': { value: '{colors.signalYellow.800}' },
    danger: { value: '{colors.red.500}' },
    'danger-hover': { value: '{colors.red.400}' },
    'danger-active': { value: '{colors.red.600}' },
    'danger-disabled-bg': { value: '{colors.red.900}' },
    'danger-disabled-text': { value: '{colors.red.800}' },
    'secondary-border': { value: '{colors.darkBlue.50}' },
    'secondary-text': { value: '{colors.darkBlue.50}' },
    'secondary-hover-bg': { value: 'rgba(0, 24, 36, 0.10)' },
    'secondary-hover-border': { value: '{colors.ghostProtocol.300}' },
    'secondary-active-bg': { value: 'rgba(0, 30, 45, 0.15)' },
    'secondary-active-border': { value: '{colors.ghostProtocol.400}' },
    'secondary-active-text': { value: '{colors.darkBlue.50}' },
    'secondary-disabled-border': { value: '{colors.ghostProtocol.700}' },
    'secondary-disabled-text': { value: '{colors.ghostProtocol.600}' },
    'tertiary-border': { value: '{colors.duskBlue.700}' },
    'tertiary-text': { value: '{colors.ghostProtocol.400}' },
    'tertiary-hover-bg': { value: 'rgba(9, 39, 56, 0.10)' },
    'tertiary-hover-border': { value: '{colors.duskBlue.50}' },
    'tertiary-active-bg': { value: 'rgba(12, 49, 69, 0.15)' },
    'tertiary-active-border': { value: '{colors.duskBlue.500}' },
    'tertiary-active-text': { value: '{colors.ghostProtocol.300}' },
    'tertiary-disabled-border': { value: '{colors.duskBlue.800}' },
    'tertiary-disabled-text': { value: '{colors.ghostProtocol.700}' },
    'focus-ring': { value: '{colors.dawnBlue.400}' },
    // Text-selection (::selection) highlight. One step lighter than the
    // light-mode wash so the selection pops on dark surfaces; text flips
    // dark so it stays legible on the light fill.
    'text-selection-bg': { value: '{colors.dawnBlue.100}' },
    'text-selection-fg': { value: '{colors.darkBlue.800}' },
    'toggle-on': { value: '{colors.signalYellow.400}' },
    // Selected radio ring + dot — Dark.tokens.json interactive/radio-on
    // aliases {brand.primary}.
    'radio-on': { value: '{colors.signalYellow.400}' },
    'nav-active': { value: '{colors.signalYellow.400}' },
    'selected-row': { value: '{colors.dawnBlue.900}' },
    active: { value: '{colors.signalYellow.400}' },
    create: { value: '{colors.signalYellow.400}' },
    // Background fill on Hover state of Icon Button. One tonal step lighter than bg/table-row-hover
    // so kebabs and other Icon Buttons hovered inside hovered rows still register visually.
    'icon-button-hover-bg': { value: '{colors.darkBlue.600}' },
    // Background fill on Pressed state of Icon Button. One step lighter than hover
    // (darkblue/500 vs darkblue/600) so the press registers as escalation in any row context.
    'icon-button-pressed-bg': { value: '{colors.darkBlue.500}' },
  },

  // ─── Border / Divider ─────────────────────────────────────────────────────────
  border: {
    default: { value: '{colors.ghostProtocol.800}' },
    subtle: { value: '{colors.duskBlue.700}' },
    strong: { value: '{colors.ghostProtocol.600}' },
    glow: { value: '{colors.dawnBlue.400}' },
    input: { value: '{colors.ghostProtocol.700}' },
    'input-hover': { value: '{colors.duskBlue.700}' },
    'input-focus': { value: '{colors.darkBlue.400}' },
    'input-error': { value: '{colors.red.600}' },
    // Edge of the organization-wide policy surface (see bg/org-policy). A
    // muted green, quieter than status/success. Prototype value, not in the
    // Design Library yet; reconcile when designed.
    'org-policy': { value: '#2c5a3a' },
    error: { value: '{colors.red.500}' },
    // Ring around TODAY's date when it is not the selected day. Dawn-blue
    // outline, distinct from the filled selected state.
    'calendar-today': { value: '{colors.dawnBlue.400}' },
    // Unchecked checkbox outline. Aliases border/strong per Dark.tokens.json:
    // interactive/tertiary-border (duskBlue.700) is 1.2:1 against
    // bg.card-elevated and fails the WCAG 3:1 non-text contrast minimum;
    // ghostProtocol.600 clears 3:1 on every dark surface.
    'checkbox-off': { value: '{colors.ghostProtocol.600}' },
    chip: { value: '{colors.dawnBlue.600}' },
    search: { value: '{colors.duskBlue.600}' },
    'section-divider': { value: '{colors.duskBlue.600}' },
    'menu-divider': { value: '{colors.darkBlue.300}' },
    topbar: { value: '{colors.darkBlue.500}' },
    'tab-toggle': { value: '{colors.darkBlue.400}' },
    'action-bar': { value: '{colors.duskBlue.600}' },
    'label-tooltip': { value: '{colors.duskBlue.600}' },
  },

  // ─── Status / Feedback ────────────────────────────────────────────────────────
  status: {
    success: { value: '{colors.green.500}' },
    'success-bg': { value: '{colors.green.900}' },
    'success-icon': { value: '{colors.green.300}' },
    'success-text': { value: '{colors.green.200}' },
    warning: { value: '{colors.orange.400}' },
    'warning-bg': { value: '{colors.orange.900}' },
    'warning-text': { value: '{colors.orange.200}' },
    error: { value: '{colors.red.500}' },
    'error-bg': { value: '{colors.red.900}' },
    'error-text': { value: '{colors.red.200}' },
    info: { value: '{colors.dawnBlue.400}' },
    'info-bg': { value: '{colors.duskBlue.900}' },
    'info-icon': { value: '{colors.duskBlue.500}' },
    'info-text': { value: '{colors.dawnBlue.200}' },
    neutral: { value: '{colors.ghostProtocol.500}' },
    'neutral-bg': { value: '{colors.ghostProtocol.800}' },
    'incident-badge': { value: '{colors.darkBlue.50}' },
    'incident-badge-topbar': { value: '{colors.signalYellow.400}' },
    // Tour / task lifecycle
    'active-bg': { value: '#1a2531' },
    'active-text': { value: '#3d7fce' },
    'in-progress-bg': { value: '#1b2e2e' },
    'in-progress-text': { value: '#16b3b0' },
    'resolved-bg': { value: '{colors.beaconGreen.deep-mint}' },
    'resolved-text': { value: '{colors.beaconGreen.500}' },
    'workflow-draft': { value: '{colors.darkBlue.300}' },
    'workflow-published': { value: '{colors.green.400}' },
    'workflow-archived': { value: '{colors.ghostProtocol.500}' },
  },

  // ─── Foreground ──────────────────────────────────────────────────────────────
  fg: {
    'toggle-knob': { value: '{colors.darkBlue.900}' },
  },

  // ─── Brand ────────────────────────────────────────────────────────────────────
  brand: {
    primary: { value: '{colors.signalYellow.400}' },
    glow: { value: '{colors.dawnBlue.400}' },
    dark: { value: '{colors.darkBlue.900}' },
    overwatch: { value: '{colors.overwatch.500}' },
  },

  // ─── Floor plan ─────────────────────────────────────────────────────────────
  floorplan: {
    'canvas-bg': { value: '{colors.darkBlue.600}' },
    'checkpoint-action-hit-area': { value: '{colors.transparent}' },
    'marker-bg': { value: '{colors.darkBlue.800}' },
    'marker-ring': { value: '{colors.darkBlue.50}' },
    'marker-glyph': { value: '{colors.darkBlue.50}' },
    'cone-fill': { value: '{colors.darkBlue.300}' },
    'cone-fill-focused': { value: '{colors.darkBlue.300}' },
    'cone-edge': { value: '{colors.darkBlue.300}' },
    'edit-frame': { value: '{colors.signalYellow.400}' },
  },

  // ─── Device association ─────────────────────────────────────────────────────
  device: {
    'associated-bg': { value: '{colors.beaconGreen.400}' },
    'associated-icon': { value: '{colors.beaconGreen.deep-teal}' },
    'grid-enabled-bg': { value: '{colors.beaconGreen.500}' },
    'grid-enabled-icon': { value: '{colors.beaconGreen.deep-mint}' },
    'tailgate-enabled-bg': { value: '{colors.beaconGreen.600}' },
    'tailgate-enabled-icon': { value: '{colors.beaconGreen.deep-lime}' },
    'ready-to-associate-bg': { value: '{colors.dawnBlue.400}' },
    'ready-to-associate-icon': { value: '{colors.darkBlue.800}' },
  },

  // ─── Icon ─────────────────────────────────────────────────────────────────────
  icon: {
    primary: { value: '{colors.darkBlue.50}' },
    secondary: { value: '{colors.darkBlue.300}' },
    // Plain glyphs on a neutral surface, matching the page foreground the way
    // text/primary does — distinct from `secondary`, which is deliberately
    // muted. Figma: icon/tertiary (Dark → darkblue/050).
    tertiary: { value: '{colors.darkBlue.50}' },
    active: { value: '{colors.signalYellow.400}' },
    danger: { value: '{colors.red.400}' },
    'on-accent': { value: '{colors.darkBlue.900}' },
    'priority-active': { value: '{colors.darkBlue.50}' },
    'priority-inactive': { value: '{colors.darkBlue.400}' },
    disabled: { value: '{colors.ghostProtocol.600}' },
    topbar: { value: '{colors.darkBlue.50}' },
    'checkbox-on': { value: '{colors.darkBlue.50}' },
  },

  // ─── Device connection health ─────────────────────────────────────────────────
  'device-connection': {
    online: { value: '{colors.green.500}' },
    'online-halo': { value: '{colors.green.900}' },
    warning: { value: '{colors.orange.500}' },
    'warning-halo': { value: '{colors.orange.900}' },
    error: { value: '{colors.red.500}' },
    'error-halo': { value: '{colors.red.900}' },
  },
})
